import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import { PdfmeService as BasePdfmeService } from '~ngx-shared/print';
import { PrintTemplateFieldModel, PrintTypeModel } from '../../models';
import type { Template } from '@pdfme/common';

@Injectable({ providedIn: 'root' })
export class PdfmeService extends BasePdfmeService {
  getTemplateTypes(): Observable<PrintTypeModel[]> {
    return this.apollo
      .query<{ result: PrintTypeModel[] }>({
        query: gql`
          query ReadPrintTemplateTypes {
            result: print_print_type(order_by: { name: asc }) {
              id
              name
              short
              is_static
              namespace
            }
          }
        `
      })
      .pipe(map(queryResult => queryResult.data?.result || []));
  }

  getTemplateTypeOptions(): Observable<{ label: string; value: number }[]> {
    return this.getTemplateTypes().pipe(
      map(types =>
        types.map(type => ({
          label: 'shared.print_type.' + type.name,
          value: type.id!
        }))
      )
    );
  }

  getTemplateNamespaceOptions(): Observable<{ label: string; value: string }[]> {
    return this.getTemplateTypes().pipe(
      map(types => [...new Set(types.map(type => type.namespace!))].filter(Boolean).sort()),
      map(namespaces =>
        namespaces.map(namespace => ({
          label: 'shared.print_namespace.' + namespace,
          value: namespace
        }))
      )
    );
  }

  getTemplateFields(typeId: number, referenceId?: number): Observable<PrintTemplateFieldModel[]> {
    return this.apollo
      .query<{ result: PrintTemplateFieldModel[] }>({
        query: gql`
          query ReadPrintTemplateFieldsByType($type: bigint!, $referenceId: bigint = 0) {
            result: print_print_template_field(
              where: {
                type_id: { _eq: $type }
                _or: [{ reference_id: { _is_null: true } }, { reference_id: { _eq: $referenceId } }]
              }
            ) {
              id
              field_type
              label
              content
              type_id
              default_value
            }
          }
        `,
        variables: { type: typeId, referenceId }
      })
      .pipe(map(queryResult => queryResult.data?.result || []));
  }

  // The letter paper is stamped by the pipeline at print time, so it is never part of the
  // document's own template; the designer only draws it behind the design.
  getStaticSchema(templateId: number): Observable<unknown[]> {
    return this.apollo
      .query<{ result?: { template_json: Template } }>({
        query: gql`
          query ReadStaticPrintTemplateSchema($id: bigint!) {
            result: print_print_template_by_pk(id: $id) {
              template_json
            }
          }
        `,
        variables: { id: templateId },
        fetchPolicy: 'no-cache'
      })
      .pipe(
        map(queryResult => {
          const basePdf = queryResult.data?.result?.template_json?.basePdf as
            { staticSchema?: unknown[] } | undefined;
          return basePdf?.staticSchema ?? [];
        })
      );
  }

  // A fixed element cannot be selected in the designer, and the print pipeline fills an element
  // from its own content when no value carries its name, so a new element starts editable. It also
  // starts hidden when empty, except a native table, whose content holds no expression to judge.
  override addField(
    template: Template,
    name: string,
    content?: string,
    type: string = 'text',
    pageIndex: number = 0
  ): Template {
    const updated = super.addField(template, name, content, type, pageIndex) as any;
    const page = updated.schemas[Math.min(pageIndex, updated.schemas.length - 1)];
    const added = page[page.length - 1];
    added.readOnly = false;
    added.hide_when_empty = type !== 'table';
    return updated;
  }
}
